type Stringifiable = Parameters<typeof JSON.stringify>[0];
export interface Logger {
  info: typeof console.info;
  log: typeof console.log;
  error: typeof console.error;
  warn: typeof console.warn;
}

function delay(attempt: number): number {
  if (attempt === 0) {
    return 0;
  }
  return Math.floor(2 ** (10 + attempt / 10));
}

export class SocketClient {
  socket: WebSocket | null = null;
  logname = '';
  reconnectTimerId: ReturnType<typeof setTimeout> = setTimeout(() => Function.prototype);
  reconnectionAttempt = 0;
  errorCount = 0;
  logger: Logger = console;
  url = '';
  openPromise: Promise<any> | null = null;

  constructor(url: string, logger: Logger, logname: string) {
    this.url = url;
    this.logname = logname;
    this.logger = logger;
    // This should happen automatically but I see this present in mmsocket
    // and I don't want to cause some weird breakage.
    if (window.addEventListener) {
      window.addEventListener('beforeunload', () => {
        if (this.socket) {
          this.socket.close();
        }
      });
    }
  }

  _onOpened() {
    this.logger.info(this.logname + ': Socket opened');
    clearTimeout(this.reconnectTimerId);
    this.reconnectionAttempt = 0;
    this.errorCount = 0;
    if (this.socket) {
      this.socket.onclose = () => this._onClosed();
    } else {
      throw new Error('Cannot set onclosed for reconnection because there is no socket');
    }
    this.onOpened();
  }

  _onClosed() {
    this.logger.info(this.logname + ': Socket closed');
    if (this.socket) {
      this.socket.removeEventListener('error', this._onError);
      // this.socket.removeEventListener("message", this._onClosed);
      this.socket.onclose = null;
    }
    this.socket = null;
    this.openPromise = null;
    this.onClosed(this.reconnectionAttempt);
    this.reconnectionAttempt += 1;
    if (this.reconnectionAttempt < this.bailedStateThresold) {
      this.reconnectTimerId = setTimeout(() => {
        this.connect();
      }, delay(this.reconnectionAttempt));
    } else {
      clearTimeout(this.reconnectTimerId);
      this.reconnectionAttempt = 0;
      this.disconnect();
      this.onBail();
      this.logger.info(this.logname + ': Bailing on trying to reconnect the socket');
    }
  }

  _onError() {
    this.logger.warn(this.logname + ': socket has errored out!');
    this.onError(this.errorCount);
    clearTimeout(this.reconnectTimerId);
    if (this.socket) {
      this.socket.removeEventListener('error', this._onError);
      // this.socket.removeEventListener("message", this._onClosed);
      this.socket.onclose = null;
      this.socket.close();
    } else {
      this.logger.warn(this.logname + ': Cannot close socket as its null');
    }
    this.socket = null;
    this.openPromise = null;
    this.reconnectionAttempt += 1;
    this.errorCount += 1;
    return new Promise((resolve, reject) => {
      if (this.reconnectionAttempt < this.bailedStateThresold) {
        this.reconnectTimerId = setTimeout(() => {
          this.connect().then(resolve).catch(reject);
        }, delay(this.reconnectionAttempt));
      } else {
        clearTimeout(this.reconnectTimerId);
        this.reconnectionAttempt = 0;
        this.disconnect();
        this.onBail();
        this.logger.info(this.logname + ': Bailing on trying to reconnect the socket');
      }
    });
  }

  _onMessage(event: any) {
    this.logger.info(this.logname + ' message: ' + event?.data);
    this.onMessage(event);
  }

  connect(): Promise<any> {
    this.logger.info(this.logname + ': Starting socket connection for: ' + this.logname);
    this.openPromise = this.preConnect()
      .then(() => {
        this.socket = new WebSocket(this.url);
        this.logger.info('Call made for opening websocket to: ' + this.logname + ' at ' + this.url);
        (window as any)[this.logname] = this;

        this.socket.addEventListener('message', this._onMessage.bind(this));
        return new Promise((resolve, reject) => {
          if (this.socket) {
            this.socket.addEventListener('open', event => {
              resolve(event);
              this._onOpened();
            });
            this.socket.addEventListener('error', err => {
              this.logger.error(err);
              this._onError().then(resolve);
            });
          } else {
            reject(new Error('Cannot attach open and error event handlers on socket as its null!'));
          }
        });
      })
      .catch(err => {
        this.logger.warn(this.logname + ': Error during opening socket for ', this.logname);
        this.logger.warn(this.logname + ': Retrying connection');
        if (err !== 'NO_RETRY_IN_CATCH') {
          return this.connect();
        }
      });
    return this.openPromise;
  }

  // Close the socket and do not try to reconnect
  disconnect() {
    clearTimeout(this.reconnectTimerId);
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
      this.openPromise = null;
    }
  }
  // Close the socket to immitate what happens when socket is dropped to test reconnection
  close() {
    this.socket?.close();
  }
  sendMessage(message: Stringifiable) {
    if (this.openPromise !== null) {
      return this.openPromise.then(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(message));
        } else {
          this.logger.warn(this.logname + ': Socket is null so cannot send message! ', message);
        }
      });
    }
    return new Promise((resolve, reject) => {
      this.logger.warn(this.logname + ': Sending socket message failed because it was not open! Retrying in 5 sec.');
      setTimeout(() => {
        this.sendMessage(message).then(resolve).catch(reject);
      }, 5000);
    });
  }

  get bailedStateThresold() {
    return 20;
  }

  // To be overridden by concrete classes
  preConnect() {
    return Promise.resolve();
  }

  // To be implemented by concrete classes
  //eslint-disable-next-line
  onError(reconnectionAttempt: number) {
    throw new Error('onError not implemented');
  }

  //eslint-disable-next-line
  onMessage(event: any) {
    throw new Error('onMessage not implemented');
  }

  //eslint-disable-next-line
  onClosed(reconnectionAttempt: number) {
    throw new Error('onClose not implemented');
  }

  onOpened() {
    throw new Error('onOpened not implemented');
  }

  onBail() {
    throw new Error('onBail not implemented');
  }
}
